import axios from "axios";

/**
 * Helpers for rewinding the work done inside a single exploration
 * iteration. They back the explicit recovery actions on the iteration
 * flow pages -- "Restart iteration" on Width / Color and the
 * "Discard ... and exit" option in the cancel dialog -- so the user can
 * roll an iteration back to a clean state when, for example, the
 * cluster image they uploaded turned out to be wrong.
 */

/**
 * Roll back every patch group's *width* (cluster) measurement for this
 * iteration. After this returns, the iteration is back at "ready for
 * geometric measurement" and the exploration status is set to either
 * PENDING (P0) or COLOR_MEASUREMENT_DONE (Qn) so the home screen routes
 * the user back to Geometric Measurement on the next Continue click.
 *
 * @param {string} explorationId
 * @param {number|string} iterationNumber Internal Iteration.iteration_number
 * @returns {Promise<void>}
 */
export async function revertIterationWidthMeasurement(
    explorationId,
    iterationNumber
) {
    if (!explorationId || iterationNumber == null) return;
    await axios.post("api/revert/width/measurement", {
        explorationId,
        iterationNumber,
        // batch_number omitted -> backend reverts every patch group in the
        // iteration's input collection.
    });
}

/**
 * Roll back every patch group's *color* measurement for this iteration.
 * After this returns, the iteration is back to "width measured, ready to
 * mark color patches".
 *
 * @param {string} explorationId
 * @param {number|string} iterationNumber Internal Iteration.iteration_number
 * @returns {Promise<void>}
 */
export async function revertIterationColorMeasurement(
    explorationId,
    iterationNumber
) {
    if (!explorationId || iterationNumber == null) return;
    await axios.post("api/revert/color/measurement", {
        explorationId,
        iterationNumber,
    });
}

/**
 * "Restart this iteration" — discard whatever has been measured in the
 * current iteration so the user can begin again from Geometric Measurement.
 * Color measurements (if any) are reverted first, then width measurements,
 * because color rows reference the width-side patch groups.
 *
 * A color-revert failure aborts the whole rollback (the error propagates
 * to the caller). The color revert is what deletes the next iteration row
 * and resets this iteration to PENDING; continuing with the width revert
 * after it failed halfway leaves the iteration counter pointing one past
 * the plate that was actually marked, while the UI looks like a clean
 * rollback.
 *
 * Pass {hasColorWork: false} to skip the color revert when the caller is
 * sure no color measurement has happened yet (e.g. the Width page) — this
 * just avoids one redundant API call.
 *
 * @param {object} args
 * @param {string} args.explorationId
 * @param {number|string} args.iterationNumber
 * @param {boolean} [args.hasColorWork=true]
 */
export async function restartIteration({
    explorationId,
    iterationNumber,
    hasColorWork = true,
}) {
    if (hasColorWork) {
        await revertIterationColorMeasurement(explorationId, iterationNumber);
    }
    await revertIterationWidthMeasurement(explorationId, iterationNumber);
}
